import PortalDialog, {PortalDialogProperties, PortalDialogState} from "../PortalDialog";
import React, {createRef, ReactNode, RefObject} from "react";
import {Dialog} from "primereact/dialog";
import PortalDialogType from "../../PortalDialogType";
import {Button} from "primereact/button";
import Parameter from "../../parameter/Parameter";
import PortalDocument from "../../../document/PortalDocument";
import MimeTypeFilter from "../../../document/mimetype/MimeTypeFilter";
import MimeTypes from "../../../document/mimetype/MimeTypes";
import {WebServiceType, WebServiceTypes} from "@softvision/webpdf-wsclient-9-typescript";
import PortalDocumentManager from "../../../document/PortalDocumentManager";
import DialogDefault from "../../parameter/PortalDialogDefault";
import DomHelper from "../../../../shared/helper/DomHelper";
import MimeType from "../../../document/mimetype/MimeType";
import CertificateEncryptDialogFields from "../../field/security/CertificateEncryptDialogFields";
import {Dropdown} from "primereact/dropdown";
import {Accordion, AccordionTab} from "primereact/accordion";
import Exception from "../../../../shared/exception/Exception";
import {Menu} from "primereact/menu";
import FileInput from "../../../../shared/components/FileInput";
import CertificateEncryptAddDialog from "./CertificateEncryptAddDialog";
import Message from "../../../../shared/log/Message";
import {MessageType} from "../../../../shared/log/MessageInterface";
import {
	AdminWebserviceStatus,
	BaseToolbox,
	CertificateEntry,
	CertificateEntryInterface,
	EncryptCertificate,
	FileDataSource,
	Functions,
	Information,
	PdfContentEncryption,
	PdfEncryptionKey,
	Recipient,
	RecipientInterface,
	Settings,
	SettingsInterface,
	SignatureEditProfile,
	ToolboxSecuritySecurity,
	UserCertificates
} from "@softvision/webpdf-wsclient-9-typescript/lib/generated-sources";
import PortalDialogSecurity, {Intention} from "../../PortalDialogSecurity";
import CertificateDialog from "./CertificateDialog";
import {MenuItem} from "primereact/menuitem";
import Translate from "../../../../shared/translation/Translate";
import Helper from "../../../../shared/components/Helper";
import {Base64} from "js-base64";
import {MultiSelect} from "primereact/multiselect";
import Icon from "../../../../shared/components/Icon";
import Immutable from "../../../../shared/immutable/Immutable";
import FunctionHelper from "../../../../shared/helper/FunctionHelper";

export enum PrintingType {
	NONE = "none",
	LOW = "low",
	HIGH = "high"
}

export interface CertificateEncryptDialogProperties extends PortalDialogProperties<ToolboxSecuritySecurity> {
	userCertificates: Immutable<UserCertificates>;
}

export interface CertificateEncryptDialogState extends PortalDialogState<ToolboxSecuritySecurity> {
	showAddDialog: boolean;
	showCertificateDialog: boolean;
	selectedCertificates: Array<CertificateEntry>;
	clipboardReadable: boolean;
}

export class CertificateEncryptDialogDefinition extends PortalDialog<ToolboxSecuritySecurity,
	CertificateEncryptDialogProperties, CertificateEncryptDialogState> {
	private certificateFile: RefObject<FileInput>;
	private menu: RefObject<Menu>;
	private menuId: string;

	constructor(props: any) {
		super(props);

		this.state = {
			...this.state,
			showAddDialog: false,
			showCertificateDialog: false,
			clipboardReadable: false,
			fieldErrors: new Map<CertificateEncryptDialogFields, string>(),
			selectedCertificates: []
		};

		this.certificateFile = createRef<FileInput>();
		this.menu = createRef<Menu>();
		this.menuId = DomHelper.generateUUID();

		this.onCertificate = this.onCertificate.bind(this);
		this.onDrop = this.onDrop.bind(this);
	}

	public static isEnabled(portalInfo: Information): boolean {
		return FunctionHelper.applyValue(
			portalInfo.webserviceStatus?.webservices?.toolbox?.enabled, AdminWebserviceStatus.getEnabledDefault()
		) && FunctionHelper.applyValue(
			portalInfo.functionsStatus?.encryptDocument, Functions.getEncryptDocumentDefault()
		);
	}

	public static isActive(
		selectedDocuments: Array<PortalDocument>, portalInfo: Information, signatureEditProfile: SignatureEditProfile
	): boolean {
		let mimeTypes: Array<MimeType> = [];

		for (let selectedDocument of selectedDocuments) {
			mimeTypes.push(selectedDocument.getMimeType());

			if (selectedDocument.isLocked()) {
				return false;
			}

			if (!PortalDialogSecurity.checkRestrictions(
				PortalDialogType.CERTIFICATE_ENCRYPT, signatureEditProfile, Intention.editingContent, selectedDocument
			)) {
				return false;
			}

			if (!selectedDocument.isEditable()) {
				return false;
			}
		}

		return this.isEnabled(portalInfo) && selectedDocuments.length === 1 &&
			MimeTypeFilter.checkSelection(mimeTypes, [MimeTypes.PDF]);
	}

	private renderRecipient(key: number, parameter: Recipient): ReactNode {
		let printing: PrintingType = PrintingType.NONE;

		if (parameter.canPrint) {
			printing = PrintingType.LOW
		}

		if (parameter.canPrintHighRes) {
			printing = PrintingType.HIGH
		}

		let certificate: CertificateEntry = CertificateEntry.fromJson({
			certificate: parameter.certificate?.value
		} as CertificateEntryInterface);
		let parsedCertificate: x509.X509Certificate | undefined;
		let certificateEntry: CertificateEntry;

		try {
			// test certificate for errors
			parsedCertificate = new x509.X509Certificate(certificate.certificate);
			certificateEntry = new CertificateEntry({
				aliasName: "",
				certificate: parameter.certificate?.value,
				hasPrivateKey: false
			} as CertificateEntryInterface);
		} catch (e: any) {
			return null;
		}

		let permissions: Map<CertificateEncryptDialogFields, boolean> =
			new Map<CertificateEncryptDialogFields, boolean>([
				[
					CertificateEncryptDialogFields.CAN_MODIFY,
					FunctionHelper.applyValue(parameter.canModify, Recipient.getCanModifyDefault())
				],
				[
					CertificateEncryptDialogFields.CAN_ASSEMBLE,
					FunctionHelper.applyValue(parameter.canAssemble, Recipient.getCanAssembleDefault())
				],
				[
					CertificateEncryptDialogFields.CAN_FILL_IN_FORM,
					FunctionHelper.applyValue(parameter.canFillInForm, Recipient.getCanFillInFormDefault())
				],
				[
					CertificateEncryptDialogFields.CAN_MODIFY_ANNOTATIONS,
					FunctionHelper.applyValue(
						parameter.canModifyAnnotations, Recipient.getCanModifyAnnotationsDefault()
					)
				],
				[
					CertificateEncryptDialogFields.CAN_EXTRACT_CONTENT,
					FunctionHelper.applyValue(parameter.canExtractContent, Recipient.getCanExtractContentDefault())
				],
				[
					CertificateEncryptDialogFields.CAN_EXTRACT_FOR_ACCESSIBILITY,
					FunctionHelper.applyValue(
						parameter.canExtractForAccessibility, Recipient.getCanExtractForAccessibilityDefault()
					)
				]
			]);

		let permissionsValue: Array<CertificateEncryptDialogFields> = [];
		for (let [field, value] of permissions.entries()) {
			if (value) {
				permissionsValue.push(field);
			}
		}

		return (
			<AccordionTab key={key} headerTemplate={
				<>
					<span className="content">{parsedCertificate.subject}</span>
					<div>
						<Button icon="icon icon-info"
								onClick={(e) => {
									e.preventDefault();
									e.stopPropagation();

									this.setState({
										showCertificateDialog: true,
										selectedCertificates: [certificateEntry]
									});
								}}/>
						<Button icon="icon icon-delete small"
								onClick={(e) => {
									e.preventDefault();
									e.stopPropagation();

									this.onChange(CertificateEncryptDialogFields.REMOVE_RECEIPIENT, key)
								}}/>
					</div>
				</>
			}>
				<fieldset className="p-col-12 p-grid">
					<div className="p-col-12 p-lg-6 p-grid"
						 hidden={!this.state.fields?.contains(CertificateEncryptDialogFields.PRINTING)}>
						<label className="p-col-12 p-sm-5" htmlFor={"control-printing-" + key}>
							<Translate {...this.props} data="certificate_encrypt.printing.label" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7 p-grid">
							<Dropdown value={printing} inputId={"control-printing-" + key}
									  options={[
										  {
											  label: Translate.value(
												  "certificate_encrypt.printing.none", {ns: "dialog"}
											  ),
											  value: PrintingType.NONE
										  },
										  {
											  label: Translate.value(
												  "certificate_encrypt.printing.low", {ns: "dialog"}
											  ),
											  value: PrintingType.LOW
										  },
										  {
											  label: Translate.value(
												  "certificate_encrypt.printing.high", {ns: "dialog"}
											  ),
											  value: PrintingType.HIGH
										  }
									  ]}
									  onChange={(e) => this.onChange(
										  CertificateEncryptDialogFields.PRINTING, e.value, key
									  )}
									  appendTo={document.getElementById("root")}/>
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid">
						<label className="p-col-12 p-sm-5" htmlFor="control-permissions">
							<Translate {...this.props} data="certificate_encrypt.permissions" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7 p-grid">
							<MultiSelect display="chip" inputId="control-permissions" value={permissionsValue}
										 className="text-chips"
										 onChange={(e) => {
											 for (let permission of Array.from(permissions.keys())) {
												 this.onChange(
													 permission as CertificateEncryptDialogFields,
													 e.value.includes(permission),
													 key
												 );
											 }
										 }}
										 options={[
											 {
												 label: Translate.value(
													 "certificate_encrypt.changeing.modify", {ns: "dialog"}
												 ),
												 value: CertificateEncryptDialogFields.CAN_MODIFY
											 },
											 {
												 label: Translate.value(
													 "certificate_encrypt.changeing.idr", {ns: "dialog"}
												 ),
												 value: CertificateEncryptDialogFields.CAN_ASSEMBLE
											 },
											 {
												 label: Translate.value(
													 "certificate_encrypt.changeing.fs", {ns: "dialog"}
												 ),
												 value: CertificateEncryptDialogFields.CAN_FILL_IN_FORM
											 },
											 {
												 label: Translate.value(
													 "certificate_encrypt.changeing.cfs", {ns: "dialog"}
												 ),
												 value: CertificateEncryptDialogFields.CAN_MODIFY_ANNOTATIONS
											 },
											 {
												 label: Translate.value(
													 "certificate_encrypt.changeing.copy", {ns: "dialog"}
												 ),
												 value: CertificateEncryptDialogFields.CAN_EXTRACT_CONTENT
											 },
											 {
												 label: Translate.value(
													 "certificate_encrypt.changeing.usability",
													 {ns: "dialog"}
												 ),
												 value: CertificateEncryptDialogFields.CAN_EXTRACT_FOR_ACCESSIBILITY
											 },
										 ]}/>
						</div>
					</div>
				</fieldset>
			</AccordionTab>
		);
	}

	public renderContents(): ReactNode {
		let parameter: EncryptCertificate | undefined = this.state.parameter?.data.encryptCertificate;

		if (!FunctionHelper.exists(parameter)) {
			return null;
		}

		let recipientElements: Array<ReactNode> = [];
		for (let [key, recipient] of FunctionHelper.applyValue(
			parameter?.recipient, EncryptCertificate.getRecipientDefault()
		).entries()) {
			recipientElements.push(this.renderRecipient(key, recipient));
		}

		let menuItems: Array<MenuItem> = [];
		let userCertificates = UserCertificates.fromJson(this.props.userCertificates.get());
		let certificates: Array<CertificateEntry> = FunctionHelper.applyValue(
			userCertificates.certificates, UserCertificates.getCertificatesDefault()
		);

		for (let certificate of certificates) {
			let entry: CertificateEntry = CertificateEntry.fromJson(certificate);
			let parsedCertificate: x509.X509Certificate;

			try {
				parsedCertificate = new x509.X509Certificate(entry.certificate);
			} catch (e: any) {
				continue;
			}

			menuItems.push({
				label: parsedCertificate.subject,
				icon: entry.hasPrivateKey ? "icon icon-locked" : "icon empty",
				template: () => {
					return (
						<div className={"p-menuitem-link"} onClick={(event) => {
							let content: string = certificate.certificate!;

							this.onChange(
								CertificateEncryptDialogFields.ADD_RECEIPIENT,
								CertificateEntry.fromJson({
									certificate: content
								} as CertificateEntry)
							);

							this.menu.current!.hide(event);
						}}>
							<Icon icon={certificate.keyStoreName === "" ? "icon-globe small" : "icon-user"}
								  tooltip={
									  <Translate {...this.props} data={
										  certificate.keyStoreName === "" ?
											  "certificate_encrypt.global" :
											  "certificate_encrypt.user"
									  } namespace="dialog"/>
								  }/>
							{parsedCertificate.subject}
						</div>
					);
				}
			});
		}

		return (
			<>
				<fieldset className="p-col-12 p-grid">
					<div className="p-col-12 p-lg-6 p-grid"
						 hidden={!this.state.fields?.contains(CertificateEncryptDialogFields.KEY)}>
						<label className="p-col-12 p-sm-5" htmlFor="control-key">
							<Translate {...this.props} data="certificate_encrypt.key.label" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7 p-grid">
							<Dropdown inputId="control-key"
									  value={FunctionHelper.applyValue(
										  parameter?.encryptionKey, EncryptCertificate.getEncryptionKeyDefault()
									  )}
									  options={[
										  {
											  label: Translate.value(
												  "certificate_encrypt.key.40_rc4", {ns: "dialog"}
											  ),
											  value: PdfEncryptionKey.RC440
										  },
										  {
											  label: Translate.value(
												  "certificate_encrypt.key.128_rc4", {ns: "dialog"}
											  ),
											  value: PdfEncryptionKey.RC4128
										  },
										  {
											  label: Translate.value(
												  "certificate_encrypt.key.128_aes", {ns: "dialog"}
											  ),
											  value: PdfEncryptionKey.AES128
										  },
										  {
											  label: Translate.value(
												  "certificate_encrypt.key.256_aes", {ns: "dialog"}
											  ),
											  value: PdfEncryptionKey.AES256
										  }
									  ]}
									  onChange={(e) => this.onChange(
										  CertificateEncryptDialogFields.KEY, e.value
									  )}
									  appendTo={document.getElementById("root")}/>
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid"
						 hidden={!this.state.fields?.contains(CertificateEncryptDialogFields.CONTENT_ENCRYPTION)}>
						<label className="p-col-12 p-sm-5" htmlFor="control-content-encryption">
							<Translate {...this.props} data="certificate_encrypt.content_encryption.label"
									   namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7 p-grid">
							<Dropdown inputId="control-content-encryption"
									  value={FunctionHelper.applyValue(
										  parameter?.contentEncryption, EncryptCertificate.getContentEncryptionDefault()
									  )}
									  options={[
										  {
											  label: Translate.value(
												  "certificate_encrypt.content_encryption.everything",
												  {ns: "dialog"}
											  ),
											  value: PdfContentEncryption.EncryptEverything
										  },
										  {
											  label: Translate.value(
												  "certificate_encrypt.content_encryption.metadata",
												  {ns: "dialog"}
											  ),
											  value: PdfContentEncryption.ExposeMetadata
										  }
									  ]}
									  onChange={(e) => this.onChange(
										  CertificateEncryptDialogFields.CONTENT_ENCRYPTION, e.value
									  )}
									  appendTo={document.getElementById("root")}/>
						</div>
					</div>
					<div className="p-col-12 p-grid">
						<label className="p-col-12 label-left">
							<Translate {...this.props} data="certificate_encrypt.certificate" namespace="dialog"/>
						</label>
						<div className="p-col-12">
							<div className="accordion-box" onDrop={this.onDrop}>
								<div className="toolbar">
									<Button icon="icon icon-add" className="button-certificate-add"
											onClick={() => this.setState({
												showAddDialog: true
											})}
											tooltip={Translate.value("inputs.certificate.add")}/>
									<Button icon="icon icon-upload small"
											onClick={() => this.certificateFile.current?.showFileDialog()}
											tooltip={Translate.value("inputs.certificate.upload")}/>
									<div style={{display: "none"}}>
										<FileInput inputId="control-certificate-file" ref={this.certificateFile}
												   onChange={
													   (files) => {
														   if (files.length === 0) {
															   return;
														   }

														   let content: string = Base64.atob(files[0].value);

														   if (this.onCertificate(content)) {
															   this.onChange(
																   CertificateEncryptDialogFields.ADD_RECEIPIENT,
																   CertificateEntry.fromJson({
																	   certificate: content
																   } as CertificateEntryInterface)
															   );
														   }
													   }
												   }/>
									</div>
									{menuItems.length > 0 &&
										<>
											<Button icon="icon icon-certificate" className="button-certificate-select"
													tooltip={Translate.value("inputs.certificate.certificate")}
													onClick={(event) => this.menu.current!.toggle(event)}/>
											<Menu popup={true} ref={this.menu} id={this.menuId} model={menuItems}
												  appendTo={document.getElementById("root")}/>
										</>
									}
									{this.state.clipboardReadable &&
										<Button icon="icon icon-clipboard"
												onClick={async () => {
													let content: string = await navigator.clipboard.readText();

													if (this.onCertificate(content)) {
														this.onChange(
															CertificateEncryptDialogFields.ADD_RECEIPIENT,
															CertificateEntry.fromJson({
																certificate: content
															} as CertificateEntryInterface)
														);
													}
												}}
												tooltip={Translate.value("inputs.certificate.clipboard")}/>
									}
									<Button icon="icon icon-delete small"
											style={{
												position: "absolute",
												height: "100%",
												right: 0,
												top: 0
											}}
											disabled={FunctionHelper.applyValue(
												parameter?.recipient, EncryptCertificate.getRecipientDefault()
											).length === 0}
											onClick={() => {
												parameter!.recipient = [];

												this.setState({
													info: undefined,
													exception: undefined
												});
											}}
											tooltip={
												FunctionHelper.applyValue(
													parameter?.recipient, EncryptCertificate.getRecipientDefault()
												).length !== 0 ?
													Translate.value("inputs.certificate.clear") : undefined
											}/>
								</div>
								<div className="body">
									{FunctionHelper.applyValue(
										parameter?.recipient, EncryptCertificate.getRecipientDefault()
									).length > 0 ?
										<Accordion activeIndex={0}>{recipientElements}</Accordion> :
										<p className="no-results">
											<Translate {...this.props} data="certificate_encrypt.empty"
													   namespace="dialog"/>
										</p>
									}
								</div>
							</div>
						</div>
					</div>
				</fieldset>
			</>
		);
	}

	public renderDialog(): ReactNode {
		let classList: Array<string> = ["certificate-encrypt-dialog", "large"];
		if (this.state.submitted) {
			classList.push("submitted");
		}

		return (
			<>
				<Dialog className={classList.join(" ")} visible={this.props.visible} onHide={this.hide}
						closeOnEscape={true} header={this.renderHeader()} resizable={false}
						footer={this.renderFooter()} appendTo={document.getElementById("root")}>
					<div className="p-grid">
						{this.renderError()}
						<Helper {...this.props}>
							<Translate {...this.props} data={"dialog.certificate.general"} namespace={"help"}
									   markdown={true}/>
						</Helper>
						{this.renderInfo()}
						{this.renderContents()}
					</div>
				</Dialog>
				<CertificateEncryptAddDialog {...this.props} visible={this.state.showAddDialog}
											 submit={(value: CertificateEntry) => {
												 this.onChange(
													 CertificateEncryptDialogFields.ADD_RECEIPIENT,
													 CertificateEntry.fromJson({
														 certificate: value.certificate
													 } as CertificateEntryInterface)
												 );
											 }}
											 hide={() => this.setState({
												 showAddDialog: false
											 })}/>
				<CertificateDialog {...this.props} visible={this.state.showCertificateDialog}
								   certificates={this.state.selectedCertificates}
								   hide={() => this.setState({
									   showCertificateDialog: false
								   })}/>
			</>
		);
	}

	protected onChange(field: CertificateEncryptDialogFields, value: any, key?: number): void {
		let parameter: EncryptCertificate = this.state.parameter!.data.encryptCertificate!;
		let recipientParameter: Recipient;

		if (FunctionHelper.exists(key)) {
			recipientParameter = parameter.recipient![key!];
		}

		switch (field) {
			case CertificateEncryptDialogFields.KEY:
				parameter.encryptionKey = value;
				break;
			case CertificateEncryptDialogFields.PRINTING:
				switch (value) {
					case PrintingType.NONE:
						recipientParameter!.canPrint = false;
						recipientParameter!.canPrintHighRes = false;
						break;
					case PrintingType.LOW:
						recipientParameter!.canPrint = true;
						recipientParameter!.canPrintHighRes = false;
						break;
					case PrintingType.HIGH:
						recipientParameter!.canPrint = true;
						recipientParameter!.canPrintHighRes = true;
						break;
				}
				break;
			case CertificateEncryptDialogFields.CAN_MODIFY:
				recipientParameter!.canModify = value;
				break;
			case CertificateEncryptDialogFields.CAN_ASSEMBLE:
				recipientParameter!.canAssemble = value;
				break;
			case CertificateEncryptDialogFields.CAN_EXTRACT_CONTENT:
				recipientParameter!.canExtractContent = value;
				break;
			case CertificateEncryptDialogFields.CAN_EXTRACT_FOR_ACCESSIBILITY:
				recipientParameter!.canExtractForAccessibility = value;
				break;
			case CertificateEncryptDialogFields.CAN_FILL_IN_FORM:
				recipientParameter!.canFillInForm = value;
				break;
			case CertificateEncryptDialogFields.CAN_MODIFY_ANNOTATIONS:
				recipientParameter!.canModifyAnnotations = value;
				break;
			case CertificateEncryptDialogFields.ADD_RECEIPIENT:
				let knownCertificates: Array<string> = [];

				for (let recipient of FunctionHelper.applyValue(
					parameter.recipient, EncryptCertificate.getRecipientDefault()
				)) {
					let certificate: x509.X509Certificate;

					try {
						certificate = new x509.X509Certificate(recipient.certificate!.value);
					} catch (e: any) {
						continue;
					}

					knownCertificates.push(certificate.serialNumber);
				}

				let tempCertificate: x509.X509Certificate;
				try {
					tempCertificate = new x509.X509Certificate(value.certificate);
				} catch (e: any) {
					this.setState({
						exception: new Exception(
							Translate.value("errors.fields.certificate.invalid", {ns: "message"})
						)
					});
					break;
				}

				if (knownCertificates.indexOf(tempCertificate.serialNumber) !== -1) {
					this.setState({
						info: Translate.value("infos.certificate.duplicate", {ns: "message"})
					});
					break;
				}

				parameter.recipient?.push(Recipient.fromJson({
					canAssemble: false,
					canExtractContent: false,
					canExtractForAccessibility: false,
					canFillInForm: false,
					canModify: false,
					canModifyAnnotations: false,
					canPrint: false,
					canPrintHighRes: false,
					certificate: {
						source: FileDataSource.Value,
						value: value.certificate
					}
				} as RecipientInterface));

				this.setState({
					info: undefined,
					exception: undefined
				});
				break;
			case CertificateEncryptDialogFields.REMOVE_RECEIPIENT:
				parameter.recipient!.splice(value, 1);
				this.setState({
					info: undefined,
					exception: undefined
				});
				break;
			case CertificateEncryptDialogFields.CONTENT_ENCRYPTION:
				parameter.contentEncryption = value;
				break;
		}

		this.setState({
			parameter: this.state.parameter
		});
	}

	private async onDrop(event: React.DragEvent<HTMLDivElement>): Promise<void> {
		let files: FileList = event.dataTransfer.files;
		if (files.length === 0) {
			return;
		}

		let content: string = await files[0].text();

		if (this.onCertificate(content)) {
			this.onChange(
				CertificateEncryptDialogFields.ADD_RECEIPIENT,
				CertificateEntry.fromJson({
					certificate: content
				} as CertificateEntryInterface)
			);
		}
	}

	private onCertificate(value: string): boolean {
		let errors: Map<CertificateEncryptDialogFields, string> = this.state.fieldErrors;

		try {
			new x509.X509Certificate(value);
		} catch (e: any) {
			errors.set(CertificateEncryptDialogFields.CERTIFICATE, "errors.fields.certificate.invalid");
			this.setState({
				exception: new Exception(
					Translate.value("errors.fields.certificate.invalid", {ns: "message"})
				)
			});
			return false;
		}

		errors.delete(CertificateEncryptDialogFields.CERTIFICATE);
		return true;
	}

	protected renderHeader(): ReactNode {
		return (
			<div>
				<h1>
					<Icon icon={"icon-locked"}/>
					<Translate {...this.props} data="certificate_encrypt.title" namespace="dialog"/>
				</h1>
				<Button icon="icon icon-close" onClick={() => this.hide()}/>
				{this.renderHelpButton()}
			</div>
		);
	}

	protected isValid(): boolean {
		let parameter: EncryptCertificate | undefined = this.state.parameter?.data.encryptCertificate;
		return FunctionHelper.applyValue(parameter?.recipient, EncryptCertificate.getRecipientDefault()).length > 0;
	}

	protected async handleKeydown(event: KeyboardEvent): Promise<void> {
		let parameter: EncryptCertificate | undefined = this.state.parameter?.data.encryptCertificate;

		if (event.key.toLowerCase() === "enter" && !this.state.submitted && FunctionHelper.applyValue(
			parameter?.recipient?.length, 0
		) > 0) {
			await this.submit();
		}
	}

	/**
	 * Handle showing/initializing the dialog
	 */
	protected async onShow(): Promise<void> {
		let status: PermissionStatus | undefined;

		try {
			status = await navigator.permissions.query({name: "clipboard-read" as PermissionName});
		} catch (e: any) {
			// can be ignored permission is denied
		}

		this.setState({
			clipboardReadable: status?.state === "granted" || status?.state === "prompt"
		});

		await super.onShow();
	};

	protected async submit(): Promise<void> {
		await super.submit();

		let toolboxOperation: BaseToolbox = BaseToolbox.fromJson({
			security: this.state.parameter!.data.toJson()
		});

		let operationData: Array<BaseToolbox> = [toolboxOperation];
		let webServiceType: WebServiceType = WebServiceTypes.TOOLBOX;
		let documentManager: PortalDocumentManager = this.props.context.documentManager;
		let selection: Array<PortalDocument> = documentManager.getSelection();

		for (let portalDocument of selection) {
			try {
				await this.props.context.session.executeRequest({
					webServiceType: webServiceType,
					document: portalDocument,
					operation: operationData,
					settings: Settings.fromJson({
						pdfa: {
							pdfaEditProfile: this.props.pdfaEditProfile
						},
						signature: {
							signatureEditProfile: this.props.signatureEditProfile
						},
						compress: {
							compressProfile: this.props.compressProfile,
							compressObjects: {
								objectStreamSize: this.props.objectStreamSize
							}
						}
					} as SettingsInterface)
				});
			} catch (exception: any) {
				if (await this.onError(exception)) {
					await this.submit();
				}
				return;
			}

			let message: Message = new Message("success.toolbox.encrypt", MessageType.SUCCESS);
			message.context = {filename: '"' + portalDocument.getFileName() + '" '};
			this.props.context.getPage().addMessage(message);
			this.hide();
		}
	}

	protected getDialogType(): PortalDialogType {
		return PortalDialogType.CERTIFICATE_ENCRYPT;
	}

	protected getDefaultParameter(): Parameter<ToolboxSecuritySecurity> {
		return DialogDefault.getParameter(
			this.getDialogType(), this.props.context.documentManager.getSelection()
		);
	}
}

const CertificateEncryptDialog = React.memo(CertificateEncryptDialogDefinition,
	(
		prevProps: Readonly<CertificateEncryptDialogProperties>, nextProps: Readonly<CertificateEncryptDialogProperties>
	) => {
		if (!nextProps.visible && !prevProps.visible) {
			return true
		}

		return false;
	}
);

export default CertificateEncryptDialog;